import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NEARBY_CATEGORIES } from "@/components/NearbySection";
import { Trash2, Plus, Sparkles, Loader2 } from "lucide-react";

export function NearbyEditor({ tour, onSaved }) {
  const [items, setItems] = useState(tour.nearby?.items || []);
  const [drives, setDrives] = useState(tour.nearby?.drives || []);
  const tid = tour.id;

  const suggest = useMutation({
    mutationFn: async () => (await api.post(`/agent/tours/${tid}/nearby/suggest`)).data,
    onSuccess: (d) => { setItems(d.items); if (d.drives.length) setDrives(d.drives); toast.success("Suggestions added. Review, edit, then save."); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const save = useMutation({
    mutationFn: async () => (await api.put(`/agent/tours/${tid}/nearby`, {
      items: items.filter((i) => i.name.trim()),
      drives: drives.filter((d) => d.label.trim()).map((d) => ({ ...d, minutes: d.minutes ? Number(d.minutes) : null, miles: d.miles ? Number(d.miles) : null })),
    })).data,
    onSuccess: () => { onSaved?.(); toast.success("What's Nearby saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const upd = (list, setList, idx, k, v) => setList(list.map((x, i) => (i === idx ? { ...x, [k]: v } : x)));

  return (
    <div className="space-y-4" data-testid={`nearby-editor-${tid}`}>
      <Button variant="outline" size="sm" className="rounded-full gap-1" onClick={() => suggest.mutate()} disabled={suggest.isPending} data-testid={`nearby-suggest-${tid}`}>
        {suggest.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Auto-suggest from map
      </Button>
      <div className="space-y-2">
        <p className="text-xs font-semibold">Places</p>
        {items.map((it, i) => (
          <div key={i} className="flex flex-wrap gap-2">
            <Select value={it.category} onValueChange={(v) => upd(items, setItems, i, "category", v)}>
              <SelectTrigger className="h-9 w-32"><SelectValue /></SelectTrigger>
              <SelectContent>{NEARBY_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input className="h-9 flex-1 min-w-[140px]" placeholder="Name" value={it.name} onChange={(e) => upd(items, setItems, i, "name", e.target.value)} data-testid={`nearby-name-${tid}-${i}`} />
            <Input className="h-9 w-32" placeholder="e.g. 0.5 mi" value={it.detail || ""} onChange={(e) => upd(items, setItems, i, "detail", e.target.value)} />
            <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button variant="ghost" size="sm" className="gap-1" onClick={() => setItems([...items, { category: "Grocery", name: "", detail: "" }])} data-testid={`nearby-add-place-${tid}`}><Plus className="h-3.5 w-3.5" /> Add place</Button>
      </div>
      <div className="space-y-2">
        <p className="text-xs font-semibold">Drive times to key places</p>
        {drives.map((d, i) => (
          <div key={i} className="flex flex-wrap gap-2">
            <Input className="h-9 flex-1 min-w-[160px]" placeholder="e.g. Hickam" value={d.label} onChange={(e) => upd(drives, setDrives, i, "label", e.target.value)} data-testid={`drive-label-${tid}-${i}`} />
            <Input className="h-9 w-24" type="number" placeholder="min" value={d.minutes ?? ""} onChange={(e) => upd(drives, setDrives, i, "minutes", e.target.value)} data-testid={`drive-min-${tid}-${i}`} />
            <Input className="h-9 w-24" type="number" placeholder="miles" value={d.miles ?? ""} onChange={(e) => upd(drives, setDrives, i, "miles", e.target.value)} />
            <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => setDrives(drives.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button variant="ghost" size="sm" className="gap-1" onClick={() => setDrives([...drives, { label: "", minutes: "", miles: "" }])} data-testid={`nearby-add-drive-${tid}`}><Plus className="h-3.5 w-3.5" /> Add drive time</Button>
      </div>
      <Button size="sm" className="rounded-full" onClick={() => save.mutate()} disabled={save.isPending} data-testid={`nearby-save-${tid}`}>
        {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save What's Nearby"}
      </Button>
    </div>
  );
}
